// EditQuestionDialog.jsx - Soru düzenleme dialog'u
import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress } from '@mui/material';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { updateQuestion } from '../../redux/testSlice';
import QuestionFormFields from './QuestionFormFields';

const EditQuestionDialog = ({ open, onClose, question, testId, loading, currentTestQuestions = [] }) => {
  const dispatch = useDispatch();
  
  const [editQuestion, setEditQuestion] = useState({
    questionText: "",
    questionNumber: 1,
    questionType: "",
    options: [],
    minScale: 1,
    maxScale: 5,
    minLabel: "",
    maxLabel: ""
  });
  
  const [currentOptions, setCurrentOptions] = useState([]);
  const [newOption, setNewOption] = useState({ text: "" }); // value kaldırıldı

  useEffect(() => {
    if (question) {
      setEditQuestion({
        questionText: question.questionText,
        questionNumber: question.questionNumber,
        questionType: question.questionType,
        options: question.options ? [...question.options] : [],
        minScale: question.minScale || 1,
        maxScale: question.maxScale || 5,
        minLabel: question.minLabel || "",
        maxLabel: question.maxLabel || ""
      });
      // Redux state'inden gelen options'ı deep copy yap
      setCurrentOptions(question.options ? JSON.parse(JSON.stringify(question.options)) : []);
    }
  }, [question]);

  const handleQuestionTypeChange = (newType) => {
    setEditQuestion({...editQuestion, questionType: newType});
    setCurrentOptions([]);
    
    if (newType === "YES_NO") {
      setCurrentOptions([
        { text: "Yes", value: 1 },
        { text: "No", value: 0 }
      ]);
    }
  };

  const handleSubmit = () => {
    if (!question) {
      toast.error("Unable to update question. Please try again.");
      return;
    }

    if (!editQuestion.questionText || !editQuestion.questionType) {
      toast.error("Please enter question text and type!");
      return;
    }
    
    const normalizedQuestionNumber = Number(editQuestion.questionNumber);

    if (!Number.isInteger(normalizedQuestionNumber) || normalizedQuestionNumber < 1) {
      toast.error("Question number must be a positive whole number!");
      return;
    }

    const otherQuestionNumbers = currentTestQuestions
      .filter((q) => q._id !== question._id)
      .map((q) => q.questionNumber);

    if (otherQuestionNumbers.includes(normalizedQuestionNumber)) {
      toast.error("That question number is already in use by another question.");
      return;
    }

    if (editQuestion.questionType === "SCALE") {
      if (!editQuestion.minLabel?.trim() || !editQuestion.maxLabel?.trim()) {
        toast.error("Scale questions need both minimum and maximum labels!");
        return;
      }
    }
    
    let questionData = { ...editQuestion, questionNumber: normalizedQuestionNumber };

    if (editQuestion.questionType === "MULTIPLE_CHOICE" || editQuestion.questionType === "YES_NO") {
      if (currentOptions.length < 2) {
        toast.error("You must add at least 2 options!");
        return;
      }
      questionData.options = currentOptions;
    }

    if (editQuestion.questionType === "EMERGENCY_CONTACT") {
      questionData.questionType = "TEXT";
      questionData.questionText = `__EMERGENCY_CONTACT__\n${editQuestion.questionText}`;
      questionData.options = [];
    }
    
    dispatch(updateQuestion({
      testId: testId,
      questionId: question._id,
      questionData
    }));
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Edit Question</DialogTitle>
      <DialogContent>
        <QuestionFormFields
          question={editQuestion}
          setQuestion={setEditQuestion}
          currentOptions={currentOptions}
          setCurrentOptions={setCurrentOptions}
          newOption={newOption}
          setNewOption={setNewOption}
          onQuestionTypeChange={handleQuestionTypeChange}
          isEdit={true}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} /> : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditQuestionDialog;
